<template>
  <form :class="{ initializing: !initialized }" @submit.prevent="submit">
    <div class="intro font-md lh-tight wt-md">
      <p>
        Send us your brief and we will match you with locations that suit your
        brief, budget and timing.
        <span class="fg-primary">Let’s get started!</span>
      </p>
    </div>
    <div class="tabbed accordion formcordion">
      <internal-nav
        class="tabs compact-header"
        :active-item="activeSection"
        :items="sections"
        :disabled="!initialized"
        @select="openSectionViaTab"
      ></internal-nav>
      <div class="loader-wrapper" v-show="!initialized">
        <loader :loading="!initialized"></loader>
      </div>
      <div class="content-panes" v-if="initialized">
        <div
          class="tab-content"
          :class="{ active: activeSection === 'production' }"
          data-section="production"
        >
          <div class="content has-fixed-submit">
            <div
              class="form-group vanilla"
              :class="{ 'has-input-error': errors.production_types }"
              v-if="meta.availability_types.length"
            >
              <label class="checkbox-group-heading">
                Production type
                <span class="required">*</span>
              </label>
              <div class="checkbox-group columns-3">
                <div class="column" v-for="option in meta.availability_types">
                  <checkbox
                    :label="option.label"
                    :input-value="option.id"
                    v-model="brief.production_types"
                    @click="delete errors.production_types"
                  ></checkbox>
                </div>
              </div>
              <input-error v-if="errors.production_types">{{
                errors.production_types[0]
              }}</input-error>
            </div>
            <div class="form-section columned two-column unlined">
              <div
                class="form-group vanilla no-divider"
                :class="{ 'has-input-error': errors.production_name }"
              >
                <label for="brief-production-name">
                  Production/Campaign
                  <span class="required">*</span>
                </label>
                <input
                  id="brief-production-name"
                  type="text"
                  v-model="brief.production_name"
                  @keydown="delete errors.production_name"
                />
                <input-error v-if="errors.production_name">{{
                  errors.production_name[0]
                }}</input-error>
              </div>
              <div
                class="form-group vanilla no-divider"
                :class="{ 'has-input-error': errors.client }"
              >
                <label for="brief-client">
                  Client/Brand
                  <span class="required">*</span>
                </label>
                <input
                  id="brief-client"
                  type="text"
                  v-model="brief.client"
                  @keydown="delete errors.client"
                />
                <input-error v-if="errors.client">{{
                  errors.client[0]
                }}</input-error>
              </div>
              <div
                class="form-group vanilla no-divider"
                :class="{ 'has-input-error': errors.location }"
              >
                <label for="brief-location">
                  Region/Location
                  <span class="required">*</span>
                </label>
                <location-search
                  :placeholder="false"
                  :initial-value="brief.selected_location"
                  classes="full-width"
                  @input="setLocation"
                  @clear="clearLocation"
                  @change="delete errors.location"
                ></location-search>
                <input-error v-if="errors.location">{{
                  errors.location[0]
                }}</input-error>
              </div>
              <div
                class="form-group vanilla no-divider"
                :class="{ 'has-input-error': errors.budget }"
              >
                <label for="brief-budget">
                  Budget
                  <span class="required">*</span>
                </label>
                <input
                  id="brief-budget"
                  type="number"
                  min="0"
                  step="1"
                  v-model="brief.budget"
                  @keyup="brief.budget < 0 ? (brief.budget = '') : brief.budget"
                  @focus="delete errors.budget"
                />
                <input-error v-if="errors.budget">{{
                  errors.budget[0]
                }}</input-error>
              </div>
              <div
                class="form-group vanilla no-divider"
                :class="{
                  'has-input-error': errors.number_of_locations_required,
                }"
              >
                <label for="brief-num-locations"
                  >Number of locations required</label
                >
                <input
                  id="brief-num-locations"
                  type="number"
                  min="0"
                  step="1"
                  v-model="brief.number_of_locations_required"
                  @keyup="
                    brief.number_of_locations_required < 0
                      ? (brief.number_of_locations_required = '')
                      : brief.number_of_locations_required
                  "
                  @focus="delete errors.number_of_locations_required"
                />
                <input-error v-if="errors.number_of_locations_required"
                  >{{ errors.number_of_locations_required[0] }}
                </input-error>
              </div>
              <div
                class="form-group vanilla no-divider"
                :class="{ 'has-input-error': errors.areas_required }"
              >
                <label>Areas required</label>
                <select-wrapper
                  id="brief-areas-required"
                  :options="areasRequiredOptions"
                  v-model="brief.areas_required"
                ></select-wrapper>
                <input-error v-if="errors.areas_required">{{
                  errors.areas_required[0]
                }}</input-error>
              </div>
            </div>
            <div class="form-group submit no-divider align-center">
              <button
                type="button"
                class="button primary"
                @click="openSection({ id: 'themes' }, true, true)"
              >
                Save and go to next step
              </button>
            </div>
          </div>
        </div>
        <div
          class="tab-content"
          :class="{ active: activeSection === 'themes' }"
          data-section="themes"
        >
          <div class="content has-fixed-submit">
            <div
              class="form-group vanilla no-divider"
              :class="{ 'has-input-error': errors.property_types }"
              v-if="meta.property_types.length"
            >
              <label class="checkbox-group-heading">
                Category
                <span class="required">*</span>
              </label>
              <input-error v-if="errors.property_types">{{
                errors.property_types[0]
              }}</input-error>
              <div class="checkbox-group columns-4">
                <div class="column" v-for="option in meta.property_types">
                  <div
                    class="image-example interactive"
                    @click="toggleOption(option.id, brief.property_types)"
                  >
                    <div class="background">
                      <img
                        :src="
                          option.thumbnail
                            ? option.thumbnail
                            : '/images/placeholders/image-coming-soon.jpg'
                        "
                        class="cover"
                        :title="option.label"
                        :alt="option.label"
                      />
                    </div>
                  </div>
                  <checkbox
                    :label="option.label"
                    :input-value="option.id"
                    v-model="brief.property_types"
                    @click="delete errors.property_types"
                  ></checkbox>
                </div>
              </div>
            </div>
            <div
              class="form-group vanilla no-divider"
              :class="{ 'has-input-error': errors.exterior_styles }"
              v-if="meta.exterior_styles.length"
            >
              <label class="checkbox-group-heading">
                Exterior styles
                <span class="required">*</span>
              </label>
              <input-error v-if="errors.exterior_styles">{{
                errors.exterior_styles[0]
              }}</input-error>
              <div class="checkbox-group columns-4">
                <div class="column" v-for="option in meta.exterior_styles">
                  <div
                    class="image-example interactive"
                    @click="toggleOption(option.id, brief.exterior_styles)"
                  >
                    <div class="background">
                      <img
                        :src="
                          option.image
                            ? option.image
                            : '/images/placeholders/image-coming-soon.jpg'
                        "
                        class="cover"
                        :title="option.label"
                        :alt="option.label"
                      />
                    </div>
                  </div>
                  <checkbox
                    :label="option.label"
                    :input-value="option.id"
                    v-model="brief.exterior_styles"
                    @click="delete errors.exterior_styles"
                  ></checkbox>
                </div>
              </div>
            </div>
            <div class="form-group submit no-divider align-center">
              <button
                type="button"
                class="button tertiary"
                @click="openSection({ id: 'production' }, true)"
              >
                Go back one step
              </button>
              <button
                type="button"
                class="button primary"
                @click="openSection({ id: 'additional' }, true, true)"
              >
                Save and go to next step
              </button>
            </div>
          </div>
        </div>
        <div
          class="tab-content"
          :class="{ active: activeSection === 'additional' }"
          data-section="additional"
        >
          <div class="content has-fixed-submit">
            <div class="form-group vanilla no-divider">
              <label
                >Unique features, inspiration images or other information</label
              >
              <media-uploader
                name="inspiration"
                :multiple="true"
                :accept="['image/*']"
                :max-file-size="10240"
                :max-items="10"
                :initial-value="brief.inspiration"
                v-model="brief.inspiration"
              ></media-uploader>
            </div>
            <div
              class="form-group vanilla no-divider"
              :class="{ 'has-input-error': errors.details }"
            >
              <label for="brief-details"
                >Further production details or mandatories</label
              >
              <textarea
                id="brief-details"
                class="medium"
                v-model="brief.details"
              ></textarea>
              <input-error v-if="errors.details">{{
                errors.details[0]
              }}</input-error>
            </div>
            <div
              class="form-group vanilla no-divider no-top-margin"
              :class="{
                'has-input-error':
                  errors['preferred_dates.from'] ||
                  errors['preferred_dates.to'],
              }"
            >
              <label>Select preferred dates</label>
              <litepicker
                :inline="true"
                :multi="true"
                :range="true"
                :months="2"
                :min-date="minDate"
                :start-date="brief.preferred_dates.from"
                :end-date="brief.preferred_dates.to"
                @input="setDates"
              >
              </litepicker>
              <input-error
                v-if="
                  errors['preferred_dates.from'] || errors['preferred_dates.to']
                "
              >
                Please select your preferred dates
              </input-error>
            </div>
            <table class="summary-table">
              <tr v-if="brief.production_types.length">
                <td>Production Type(s)</td>
                <td>{{ productionTypesLabel }}</td>
              </tr>
              <tr>
                <td>Production/Campaign</td>
                <td>{{ brief.production_name }}</td>
              </tr>
              <tr>
                <td>Client/Brand</td>
                <td>{{ brief.client }}</td>
              </tr>
              <tr>
                <td>Region/Location</td>
                <td>{{ brief.selected_location.name }}</td>
              </tr>
              <tr>
                <td>Budget</td>
                <td>{{ brief.budget }}</td>
              </tr>
              <tr v-if="brief.number_of_locations_required">
                <td>Number of locations required</td>
                <td>{{ brief.number_of_locations_required }}</td>
              </tr>
              <tr v-if="brief.areas_required">
                <td>Areas required</td>
                <td>{{ meta.areas_required_options[brief.areas_required] }}</td>
              </tr>
              <tr v-if="brief.property_types.length">
                <td>Categories</td>
                <td>{{ propertyTypesLabel }}</td>
              </tr>
              <tr v-if="brief.exterior_styles.length">
                <td>Exterior Styles</td>
                <td>{{ exteriorStylesLabel }}</td>
              </tr>
              <tr v-if="Object.keys(brief.inspiration).length">
                <td>Inspiration images</td>
                <td>
                  <ul class="summary-table-images">
                    <li v-for="media in brief.inspiration">
                      <img :src="media.preview_url" :alt="media.name" />
                    </li>
                  </ul>
                </td>
              </tr>
              <tr v-if="brief.details">
                <td>Further production details</td>
                <td>{{ brief.details }}</td>
              </tr>
              <tr v-if="brief.preferred_dates.from && brief.preferred_dates.to">
                <td>Preferred Date(s)</td>
                <td>{{ dateRangeLabel }}</td>
              </tr>
            </table>
            <div
              class="form-group vanilla submit no-divider align-center"
              v-if="authed"
            >
              <loader-button
                type="submit"
                :loading="loading"
                label="Save And Send For Review"
              ></loader-button>
            </div>
            <div
              class="form-group submit no-divider align-center"
              v-if="!authed"
            >
              <button
                type="button"
                class="button tertiary"
                @click="openSection({ id: 'themes' }, true)"
              >
                Go back one step
              </button>
              <button
                type="button"
                class="button primary"
                @click="openSection({ id: 'contact' }, true)"
              >
                Save and go to next step
              </button>
            </div>
          </div>
        </div>
        <div
          class="tab-content"
          :class="{ active: activeSection === 'contact' }"
          v-if="!authed"
          data-section="contact"
        >
          <div class="content has-fixed-submit">
            <div class="intro flex">
              <div class="message flex-1 align-center">
                <p>
                  If you're an existing user of A Perfect Space, skip this step
                  by
                  <a class="pink-link" @click="showLogin">clicking here</a> to
                  log in.
                </p>
              </div>
            </div>

            <div class="form-section columned two-column unlined">
              <div
                class="form-group vanilla"
                :class="{ 'has-input-error': errors.first_name }"
              >
                <label for="brief-first-name">
                  First name
                  <span class="required">*</span>
                </label>
                <input
                  id="brief-first-name"
                  type="text"
                  v-model="brief.first_name"
                />
                <input-error v-if="errors.first_name">{{
                  errors.first_name[0]
                }}</input-error>
              </div>
              <div
                class="form-group vanilla"
                :class="{ 'has-input-error': errors.last_name }"
              >
                <label for="brief-last-name">
                  Last name
                  <span class="required">*</span>
                </label>
                <input
                  id="brief-last-name"
                  type="text"
                  v-model="brief.last_name"
                />
                <input-error v-if="errors.last_name">{{
                  errors.last_name[0]
                }}</input-error>
              </div>
              <div
                class="form-group vanilla"
                :class="{ 'has-input-error': errors.phone }"
              >
                <label for="brief-phone">
                  Contact number
                  <span class="required">*</span>
                </label>
                <input id="brief-phone" type="text" v-model="brief.phone" />
                <input-error v-if="errors.phone">{{
                  errors.phone[0]
                }}</input-error>
              </div>
              <div
                class="form-group vanilla"
                :class="{ 'has-input-error': errors.email }"
              >
                <label for="brief-email">
                  Contact email
                  <span class="required">*</span>
                </label>
                <input id="brief-email" type="email" v-model="brief.email" />
                <input-error v-if="errors.email">{{
                  errors.email[0]
                }}</input-error>
              </div>
            </div>
            <div
              v-if="brief.company !== 0"
              class="form-group vanilla"
              :class="{ 'has-input-error': errors.company }"
            >
              <label>
                Company
                <span class="required">*</span>
              </label>
              <select-wrapper
                id="company"
                name="company"
                :options="meta.companies"
                v-model="brief.company"
              ></select-wrapper>
              <input-error v-if="errors.company">{{
                errors.company[0]
              }}</input-error>
            </div>
            <transition name="fade" mode="out-in" appear :duration="250">
              <div
                v-if="brief.company === 0"
                class="form-section columned two-column unlined"
              >
                <div
                  class="form-group vanilla"
                  :class="{ 'has-input-error': errors.company_name }"
                >
                  <label for="company-name">Company name</label>
                  <input
                    id="company-name"
                    type="text"
                    name="company-name"
                    v-model="brief.company_name"
                  />
                  <input-error v-if="errors.company_name">{{
                    errors.company_name[0]
                  }}</input-error>
                </div>
                <div
                  class="form-group vanilla"
                  :class="{ 'has-input-error': errors.company_type }"
                >
                  <label>Company type</label>
                  <select-wrapper
                    id="company"
                    name="company"
                    :options="meta.company_types"
                    v-model="brief.company_type"
                  ></select-wrapper>
                  <input-error v-if="errors.company_type">{{
                    errors.company_type[0]
                  }}</input-error>
                </div>
              </div>
            </transition>
            <div class="form-section columned two-column unlined">
              <div
                class="form-group vanilla"
                :class="{ 'has-input-error': errors.password }"
              >
                <label for="sign-up-password">
                  Password
                  <span class="required">*</span>
                </label>
                <input
                  id="sign-up-password"
                  type="password"
                  name="password"
                  v-model="brief.password"
                />
                <input-error v-if="errors.password">{{
                  errors.password[0]
                }}</input-error>
              </div>
              <div
                class="form-group vanilla"
                :class="{ 'has-input-error': errors.password_confirmation }"
              >
                <label for="sign-up-confirm-password">
                  Confirm password
                  <span class="required">*</span>
                </label>
                <input
                  id="sign-up-confirm-password"
                  type="password"
                  name="confirm_password"
                  v-model="brief.password_confirmation"
                />
                <input-error v-if="errors.password_confirmation">{{
                  errors.password_confirmation[0]
                }}</input-error>
              </div>
            </div>
            <div class="form-group vanilla checkbox-container no-divider">
              <checkbox v-model="brief.agree_terms" :required="true">
                <template v-slot:checkbox-label>
                  I agree to the hirer
                  <a
                    href="/terms-and-conditions/hirer"
                    @click.stop.prevent="showHirerTermsModal"
                  >
                    terms &amp; conditions</a
                  >
                </template>
              </checkbox>
            </div>
            <div class="form-group vanilla submit no-divider align-center">
              <button
                type="button"
                class="button tertiary"
                @click="openSection({ id: 'additional' }, true)"
              >
                Go back one step
              </button>
              <loader-button
                type="submit"
                :loading="loading"
                label="Save And Send For Review"
              ></loader-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div style="display: none" v-if="$root.$data.honeypot">
      <input
        type="text"
        v-model="brief[$root.$data.honeypot.nameFieldName]"
        :name="$root.$data.honeypot.nameFieldName"
        :id="$root.$data.honeypot.nameFieldName"
      />
      <input
        type="text"
        v-model="brief[$root.$data.honeypot.validFromFieldName]"
        :name="$root.$data.honeypot.validFromFieldName"
      />
    </div>
  </form>
</template>

<script>
import BaseModal from "../Base";
import merge from "lodash/merge";
import cloneDeep from "lodash/cloneDeep";
import dayjs from "dayjs";

dayjs.extend(require("dayjs/plugin/duration"));

let initial_section = "production",
  brief = {
    first_name: null,
    last_name: null,
    phone: null,
    email: null,
    company: 0, // Setting to 0, no company is assigned for not logged in
    company_name: null,
    company_type: null,
    password: null,
    password_confirmation: null,
    production_types: [],
    production_name: null,
    client: null,
    number_of_locations_required: null,
    property_types: [],
    exterior_styles: [],
    areas_required: null,
    inspiration: {},
    details: null,
    location: null,
    selected_location: {
      location: null,
      name: null,
      type: null,
      radius: 5,
    },
    preferred_dates: {
      from: "",
      to: "",
    },
    agree_terms: false,
  };

export default {
  extends: BaseModal,
  props: {},
  data() {
    if (localStorage["newBrief"]) {
      let saved = JSON.parse(localStorage["newBrief"]);
      merge(cloneDeep(brief), saved.data);

      // if (saved.section) {
      //   initial_section = saved.section
      // }
    }

    return {
      loading: false,
      activeSection: initial_section,
      brief: cloneDeep(brief),
      initialized: false,
      errors: {},
      meta: null,
    };
  },

  computed: {
    authed() {
      return !!this.$store.getters["user/account"];
    },

    areasRequiredOptions() {
      let options = [];

      if (this.meta && this.meta.areas_required_options) {
        for (let prop in this.meta.areas_required_options) {
          options.push({
            id: prop,
            label: this.meta.areas_required_options[prop],
          });
        }
      }

      return options;
    },

    numberOnSiteOptions() {
      let options = [];

      if (this.meta && this.meta.number_of_people_options) {
        for (let prop in this.meta.number_of_people_options) {
          options.push({
            id: prop,
            label: this.meta.number_of_people_options[prop],
          });
        }
      }

      return options;
    },

    /**
     * Our form sections for our tab navigation.
     *
     * @return array
     */
    sections() {
      let items = [
        { id: "production", label: "Production Details" },
        { id: "themes", label: "Themes & Styles" },
        { id: "additional", label: "Further Details" },
        { id: "contact", label: "Details & Registration" },
      ];

      if (this.authed) {
        items.pop();

        if (this.activeSection === "contact") {
          this.activeSection = "additional";
        }
      }

      return items;
    },

    minDate() {
      return dayjs().add(1, "day").format("YYYY-MM-DD");
    },

    hasDates() {
      return (
        this.brief.preferred_dates.from !== "" &&
        this.brief.preferred_dates.to !== ""
      );
    },

    dateRange() {
      if (!this.hasDates) {
        return null;
      }

      let from = dayjs(this.brief.preferred_dates.from),
        to = dayjs(this.brief.preferred_dates.to),
        range = [];

      while (from.isBefore(to) || from.isSame(to)) {
        range.push(from);
        from = from.add(1, "day");
      }

      return range;
    },

    dateRangeLabel() {
      const format = "dddd D MMMM YYYY",
        from = dayjs(this.brief.preferred_dates.from),
        to = dayjs(this.brief.preferred_dates.to);

      if (this.brief.preferred_dates.from === this.brief.preferred_dates.to) {
        return from.format(format);
      }

      return `${from.format(format)} - ${to.format(format)}`;
    },

    productionTypesLabel() {
      return this.brief.production_types
        .map((id) => {
          let meta = this.meta.availability_types.find((o) => o.id === id);
          if (meta && typeof meta.label !== "undefined") {
            return meta.label;
          }
        })
        .join(", ");
    },

    propertyTypesLabel() {
      return this.brief.property_types
        .map((id) => {
          let meta = this.meta.property_types.find((o) => o.id === id);
          if (meta && typeof meta.label !== "undefined") {
            return meta.label;
          }
        })
        .join(", ");
    },

    exteriorStylesLabel() {
      return this.brief.exterior_styles
        .map((id) => {
          let meta = this.meta.exterior_styles.find((o) => o.id === id);
          if (meta && typeof meta.label !== "undefined") {
            return meta.label;
          }
        })
        .join(", ");
    },
  },

  watch: {
    authed(now) {
      if (now) {
        this.$store.commit("account/hide", true);
      }
    },
    brief: {
      handler() {
        localStorage["newBrief"] = JSON.stringify({
          data: this.brief,
          section: this.activeSection,
        });
      },
      deep: true,
    },
  },

  created() {
    this.$store.dispatch("intercom/track", { type: "Add Brief Opened" });
  },

  mounted() {
    this.$eventBus.$on("modalOpen", (modal) => {
      if (typeof modal.type !== "undefined" && modal.type === "brief-add") {
        this.activeSection = initial_section;
      }
    });

    //TODO show user when we are loading
    this.loading = true;
    axios
      .get("/api/form-meta/brief")
      .then((response) => {
        this.meta = response.data;
        this.initialized = true;
      })
      .catch(() => {})
      .then(() => (this.loading = false));
  },

  methods: {
    removeValidationError(errorField) {
      if (errorField !== undefined) {
        delete this.errorField;
      }
    },
    submit() {
      this.loading = true;
      this.errors = {};
      axios
        .post("/api/briefs", this.brief)
        .then((response) => {
          //this.$store.commit('user/updateBriefs', response.data)
          this.$emit("confirm", this.brief);
          this.brief = cloneDeep(brief);

          // this.$toasted.show('Your brief was created successfully.', {
          //   type: 'success'
          // })

          // Resolve when we've done our serverside stuff successfully.
          //this.$emit('confirm', this.brief)
          delete localStorage["newBrief"];

          if (this.authed) {
            this.$eventBus.$emit("accountTableReload_brief-listing");
            this.$store.commit("account/navigateTo", {
              pane: "main",
              page: "briefs",
              subpage: "index",
            });
          }

          this.$store.dispatch("intercom/track", {
            type: "Add Brief Submitted",
          });

          this.$store
            .dispatch("modals/show", {
              title: "Thanks for your Brief!",
              type: "brief-confirm",
              narrow: true,
              data: {
                search_url: response.data.search_url || null,
                has_matched_listings: !!(
                  response.data.matched_listings_count || false
                ),
              },
            })
            .then(() => {})
            .catch(() => {});

          if (typeof response.data.auth !== "undefined" && response.data.auth) {
            // Save our user details
            this.$store.commit("user/token", response.data.auth.token);
            this.$store.dispatch("user/setAuthHeader");
            this.$store.commit("user/authenticated", response.data.auth.user);

            // Set first login/should complete profile (open welcome account panel view)
            this.$store.commit("user/setShouldCompleteProfile", true);
          }
        })
        .catch((error) => {
          this.displayValidationErrors(error);
        })
        .then(() => (this.loading = false));
    },

    openSection(which, scroll, validate) {
      let success = () => {
        this.activeSection = which.id;

        if (scroll) {
          // Scroll to the clicked element
          let scrollTarget = this.$el.closest(".simplebar-content-wrapper");

          if (scrollTarget) {
            setTimeout(() => {
              scrollTarget.scrollTo({ top: 0, behavior: "smooth" });
            }, 50);
          }
        }
      };

      if (validate) {
        this.loading = true;
        this.errors = {};

        axios
          .post("/api/briefs/validate/" + this.activeSection, this.brief)
          .then((response) => {
            success();
            this.$store.dispatch("intercom/track", {
              type: "Add Brief Step",
              meta: { step: which.id },
            });
          })
          .catch((error) => {
            this.displayValidationErrors(error);
          })
          .then(() => {
            this.loading = false;
          });
      } else {
        success();
      }
    },

    displayValidationErrors(error) {
      let message = "";

      try {
        message = Object.values(error.response.data.errors)[0][0];
      } catch (e) {
        message =
          "Your brief could not be created. Please reload the page and try again.";

        this.$toasted.show(message, {
          type: "error",
        });
      }

      this.errors = error.response.data.errors || {};

      this.$nextTick(() => {
        let errorNodes = this.$el.querySelectorAll(".input-error");

        if (errorNodes[0]) {
          let tab = errorNodes[0].closest("[data-section]");

          if (tab) {
            this.openSection(
              { id: tab.attributes["data-section"].value },
              true
            );

            // Scroll to the clicked element
            let scrollTarget = this.$el.closest(".simplebar-content-wrapper");

            if (scrollTarget) {
              setTimeout(() => {
                scrollTarget.scrollTo({
                  top:
                    $(errorNodes[0]).offset().top -
                    $(scrollTarget).offset().top -
                    100,
                  behavior: "smooth",
                });
              }, 50);
            }
          }
        }
      });
    },

    openSectionViaTab(which, scroll) {
      let active_idx = 0;
      let target_idx = null;

      for (let x = 0; x < this.sections.length; x++) {
        if (this.sections[x].id == this.activeSection) {
          active_idx = x;
        }

        if (this.sections[x].id == which.id) {
          target_idx = x;
        }
      }

      if (target_idx !== null && target_idx < active_idx) {
        this.openSection(which, scroll);
      }
    },

    showHirerTermsModal() {
      window.open("/pdf/hirer-agreement.pdf", '_blank');
    },

    toggleOption(value, dst) {
      let existing_idx = dst.indexOf(value);

      if (existing_idx !== -1) {
        dst.splice(existing_idx, 1);
      } else {
        dst.push(value);
      }
    },

    setDates(input) {
      if (typeof input === "object") {
        return (this.brief.preferred_dates = input);
      }
      this.brief.preferred_dates = {
        from: input,
        to: input,
      };
    },

    setLocation(result) {
      this.brief.location = result.location;
      this.brief.selected_location = result;
    },

    clearLocation() {
      this.brief.location = null;
      this.brief.selected_location = {
        location: null,
        name: null,
        type: null,
        radius: 5,
      };
    },

    showLogin() {
      this.$store.commit("user/changeAuthMode", {
        which: "login",
        priority: true,
      });
    },
  },
};
</script>
